import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Radio, Lock, Users, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { api, formatApiError, CATEGORIES } from "@/lib/api";
import { RoomFormDialog } from "@/components/dashboard/RoomFormDialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

function RoomRow({ r, onEdit, onToggle, onDelete }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-3 rounded-2xl border border-white/5 bg-card p-3" data-testid={`dashboard-room-${r.id}`}>
      <img src={r.cover_url} alt="" className="h-20 w-full sm:w-28 rounded-xl object-cover" />
      <div className="min-w-0 flex-1">
        <p className="truncate font-bold">{r.title}</p>
        <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          <span>{CATEGORIES[r.category]}</span>
          <span className="inline-flex items-center gap-1">{r.access_type === "approval" ? <><Lock size={11} /> Com aprovação</> : "Entrada livre"}</span>
          <span className="inline-flex items-center gap-1 font-mono-k"><Users size={11} /> {r.viewer_count}</span>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <label className="flex items-center gap-2 text-xs font-semibold">
          {r.is_live ? <span className="inline-flex items-center gap-1 text-red-400"><Radio size={12} className="live-dot" /> Ao vivo</span> : "Offline"}
          <Switch checked={r.is_live} onCheckedChange={(v) => onToggle(r, v)} data-testid={`room-toggle-live-${r.id}`} />
        </label>
        <Link to={`/sala/${r.id}`} className="grid h-9 w-9 place-items-center rounded-full hover:bg-white/10" data-testid={`room-open-${r.id}`}><ExternalLink size={15} /></Link>
        <button onClick={() => onEdit(r)} className="grid h-9 w-9 place-items-center rounded-full hover:bg-white/10" data-testid={`room-edit-${r.id}`}><Pencil size={15} /></button>
        <button onClick={() => onDelete(r)} className="grid h-9 w-9 place-items-center rounded-full text-red-400 hover:bg-red-600 hover:text-white" data-testid={`room-delete-${r.id}`}><Trash2 size={15} /></button>
      </div>
    </div>
  );
}

export function RoomsManager({ canCreate }) {
  const [rooms, setRooms] = useState([]);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const load = () => api.get("/institution/rooms").then((r) => setRooms(r.data));
  useEffect(() => { load(); }, []);

  const save = async (data) => {
    try {
      if (editing?.id) await api.patch(`/rooms/${editing.id}`, data);
      else await api.post("/rooms", data);
      toast.success("Sala salva");
      setEditing(null);
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.guard) toast.error("Recusado pelo Guarda-Costas IA", { description: d.msg, duration: 8000 });
      else toast.error(formatApiError(e));
    }
  };
  const toggle = async (r, v) => {
    setRooms((prev) => prev.map((x) => (x.id === r.id ? { ...x, is_live: v } : x)));
    try { await api.patch(`/rooms/${r.id}`, { is_live: v }); } catch (e) { toast.error(formatApiError(e)); load(); }
  };
  const remove = async () => {
    const r = deleting;
    setDeleting(null);
    setRooms((prev) => prev.filter((x) => x.id !== r.id));
    try {
      await api.delete(`/rooms/${r.id}`);
      toast.success("Sala excluída");
    } catch (e) {
      toast.error(formatApiError(e));
      load();
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{rooms.length} sala(s)</p>
        <Button onClick={() => setEditing({})} disabled={!canCreate} className="rounded-full" data-testid="create-room-button"><Plus size={16} className="mr-1" /> Nova sala</Button>
      </div>
      {!canCreate && <p className="rounded-xl bg-amber-500/10 p-3 text-sm text-amber-300" data-testid="create-room-locked">Envie e aguarde a aprovação do Selo de Validação para criar salas.</p>}
      {rooms.length === 0 && canCreate && <p className="py-10 text-center text-muted-foreground">Crie sua primeira sala de transmissão.</p>}
      <div className="space-y-2 pb-24 md:pb-0">{rooms.map((r) => <RoomRow key={r.id} r={r} onEdit={setEditing} onToggle={toggle} onDelete={setDeleting} />)}</div>
      {editing && <RoomFormDialog open onOpenChange={(o) => !o && setEditing(null)} initial={editing.id ? editing : null} onSave={save} />}
      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent data-testid="delete-room-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir sala?</AlertDialogTitle>
            <AlertDialogDescription>A sala “{deleting?.title}” e os seus vínculos serão apagados permanentemente. Quem estiver na live será desconectado.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-room-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-red-600 hover:bg-red-500" data-testid="delete-room-confirm">Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
